import { createContext, useContext, useEffect, useMemo, useState, useRef, useCallback } from "react";
import {
  subscribeToUserProfile,
  saveUserProfile,
  subscribeToInvoices,
  saveInvoice,
  updateInvoice as updateInvoiceFirestore,
  deleteInvoice as deleteInvoiceFirestore,
  subscribeToFiscalSettings,
  saveFiscalSettings,
  subscribeToFiscalByYear,
  saveFiscalByYear,
  subscribeToPensionPlan,
  savePensionPlan,
  subscribeToPensionContributions,
  savePensionContribution,
  deletePensionContribution as deletePensionContributionFirestore,
  migrateLocalDataToFirestore,
  subscribeToAppSettings,
  saveAppSettings,
  subscribeToClients,
  saveClient as saveClientFirestore,
  updateClient as updateClientFirestore,
  deleteClient as deleteClientFirestore,
  subscribeToTimesheets,
  saveTimesheet as saveTimesheetFirestore,
  updateTimesheet as updateTimesheetFirestore,
  deleteTimesheet as deleteTimesheetFirestore,
  subscribeToF24,
  saveF24,
  deleteF24 as deleteF24Firestore,
} from "../firebase/firestoreService";
import { migrateF24List, migrateF24Record, aggregateF24PaidByYear } from "../utils/f24Schema";

const STORAGE_KEY = "piva_app_state_v1";

const safeParse = (raw) => {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

// Legge lo stato salvato da localStorage
export const getSavedState = () => {
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw ? safeParse(raw) : null;
};

// Pulisce localStorage dopo migrazione
export const clearLocalStorage = () => {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.setItem('migrationDone', 'true');
};

// Controlla se ci sono dati locali da migrare
export const hasLocalData = () => {
  // Se migrazione già fatta, non mostrare più il modal
  if (localStorage.getItem('migrationDone') === 'true') return false;

  const saved = getSavedState();
  if (!saved) return false;
  const hasInvoices = Array.isArray(saved.invoices) && saved.invoices.length > 0;
  const hasContributions = saved.pensionPlan?.contributions?.length > 0;
  const hasProfile = saved.user && (saved.user.firstName !== "Nome" || saved.user.lastName !== "Utente");
  const hasFiscalSettings = saved.fiscalSettings && saved.fiscalSettings.pivaYearStart;
  return hasInvoices || hasContributions || hasProfile || hasFiscalSettings;
};

// Ottiene riepilogo dati locali
export const getLocalDataSummary = () => {
  const saved = getSavedState();
  if (!saved) return { invoicesCount: 0, contributionsCount: 0, hasProfile: false, hasFiscalSettings: false };
  return {
    invoicesCount: saved.invoices?.length || 0,
    contributionsCount: saved.pensionPlan?.contributions?.length || 0,
    hasProfile: saved.user && (saved.user.firstName !== "Nome" || saved.user.lastName !== "Utente"),
    hasFiscalSettings: !!saved.fiscalSettings?.pivaYearStart,
  };
};

const makeId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

// Helper per aggiungere giorni a una data
const addDaysToDate = (dateString, days) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  date.setDate(date.getDate() + days);
  return date.toISOString().split("T")[0];
};

const DEFAULT_DUE_DAYS = 15;

// Migra fatture aggiungendo dueDate se mancante
const migrateInvoices = (invoices) => {
  if (!Array.isArray(invoices)) return [];
  return invoices.map((inv) => {
    if (inv.dueDate) return inv;
    return {
      ...inv,
      dueDate: inv.issueDate ? addDaysToDate(inv.issueDate, DEFAULT_DUE_DAYS) : "",
    };
  });
};

const AppStateContext = createContext(null);

// Estrae il PRIMO gruppo di numeri dal codice fattura
// Es: "01/2026" → 1, "FAT-05-2026" → 5
// Nota: per formati tipo "2026-001" il seq deve essere salvato esplicitamente
function extractSeqFromCode(code) {
  if (!code) return 0;
  // Trova il primo gruppo di numeri nel codice
  const m = String(code).match(/(\d+)/);
  return m ? Number(m[1]) || 0 : 0;
}

function padSeq(seq, digits) {
  const s = String(seq);
  if (!digits) return s;
  return s.padStart(digits, "0");
}

function formatInvoiceCode(template, seq, year) {
  const t = template || "{SEQ}";
  const y = year || new Date().getFullYear();
  const yy = String(y).slice(-2);

  return t
    // Anno - supporta entrambi i formati
    .replaceAll("{YEAR}", String(y))
    .replaceAll("{YYYY}", String(y))
    .replaceAll("{YY}", yy)
    // Progressivo - supporta entrambi i formati
    .replaceAll("{SEQ3}", padSeq(seq, 3))
    .replaceAll("{NNN}", padSeq(seq, 3))
    .replaceAll("{SEQ2}", padSeq(seq, 2))
    .replaceAll("{NN}", padSeq(seq, 2))
    .replaceAll("{SEQ}", String(seq))
    .replaceAll("{N}", String(seq));
}

// Valori di default
const DEFAULT_USER = {
  firstName: "Nome",
  lastName: "Utente",
  logoUrl: null,
  invoiceNumberTemplate: "{SEQ}",
};

const DEFAULT_FISCAL_SETTINGS = {
  pivaYearStart: new Date().getFullYear(),
  // Data di apertura P.IVA "YYYY-MM-DD": serve al ragguaglio delle soglie nel primo
  // anno. Se vuota si assume il 1° gennaio di pivaYearStart (nessun ragguaglio).
  pivaStartDate: "",
  atecoCode: "74.90.99",
  profitabilityCoefficient: 0.78,
  taxRate: 5,
  inpsType: "gestione-separata",
  // inpsRate non è più statico - viene calcolato dinamicamente da getAliquotaINPS()
  contributiInpsPrecedenti: 0,
  codiceSedeInps: "",
};

// Struttura dati fiscali storicizzati per singolo anno (saldo/acconti).
const DEFAULT_FISCAL_YEAR = {
  contributiInpsVersati: 0,
  impostaDovuta: 0,
  incassi: 0,
  accontiVersatiManuali: 0,
  accontiVersati: { giugno: 0, novembre: 0 },
  inpsAccontiVersati: { giugno: 0, novembre: 0 },
  saldoVersato: { imposta: 0, inps: 0 },
};

const DEFAULT_PENSION_PLAN = {
  monthlyGoal: 0,
};

const DEDUCTIBILITY_LIMIT = 5300;

// Hook interno per gestire lo stato
function useAppStateInternal(userId) {
  const isAuthenticated = !!userId;

  // Stati principali
  const [user, setUserState] = useState(DEFAULT_USER);
  const [invoices, setInvoices] = useState([]);
  const [fiscalSettings, setFiscalSettingsState] = useState(DEFAULT_FISCAL_SETTINGS);
  const [pensionPlan, setPensionPlanState] = useState(DEFAULT_PENSION_PLAN);
  const [pensionContributions, setPensionContributions] = useState([]);
  const [appSettings, setAppSettings] = useState({});
  const [clients, setClients] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [f24List, setF24List] = useState([]);
  const [fiscalByYear, setFiscalByYearState] = useState({});

  // Stati UI/preferenze (sempre in localStorage)
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved !== null ? JSON.parse(saved) : false;
  });

  const [selectedYear, setSelectedYear] = useState(() => {
    const saved = getSavedState();
    return typeof saved?.selectedYear === "number" ? saved.selectedYear : new Date().getFullYear();
  });

  // Stati di sync
  const [isLoading, setIsLoading] = useState(isAuthenticated);
  const [isSynced, setIsSynced] = useState(!isAuthenticated);
  const [syncError, setSyncError] = useState(null);

  // Ref per tracciare unsubscribe
  const unsubscribeRefs = useRef([]);
  const isFirstRender = useRef(true);

  // Toggle dark mode
  const toggleDarkMode = useCallback(() => {
    setDarkMode((prev) => {
      const newValue = !prev;
      localStorage.setItem('darkMode', JSON.stringify(newValue));
      return newValue;
    });
  }, []);

  // ========== FIRESTORE SYNC ==========
  useEffect(() => {
    // Cleanup precedenti subscriptions
    unsubscribeRefs.current.forEach((unsub) => unsub());
    unsubscribeRefs.current = [];

    if (!isAuthenticated) {
      // Fallback a localStorage
      const saved = getSavedState();
      if (saved) {
        setUserState(saved.user || DEFAULT_USER);
        setInvoices(migrateInvoices(saved.invoices || []));
        setFiscalSettingsState({ ...DEFAULT_FISCAL_SETTINGS, ...saved.fiscalSettings });
        setPensionPlanState({ ...DEFAULT_PENSION_PLAN, monthlyGoal: saved.pensionPlan?.monthlyGoal || 0 });
        setPensionContributions(saved.pensionPlan?.contributions || []);
        setF24List(migrateF24List(saved.f24 || []));
        setFiscalByYearState(saved.fiscalByYear || {});
      }
      setIsLoading(false);
      setIsSynced(true);
      return;
    }

    // Authenticated: usa Firestore con listeners real-time
    setIsLoading(true);
    setSyncError(null);

    let loadedCount = 0;
    const totalToLoad = 10;

    const checkLoaded = () => {
      loadedCount++;
      if (loadedCount >= totalToLoad) {
        setIsLoading(false);
        setIsSynced(true);
      }
    };

    try {
      // Profile
      const unsubProfile = subscribeToUserProfile(userId, (data) => {
        if (data) {
          setUserState({ ...DEFAULT_USER, ...data });
        } else {
          setUserState(DEFAULT_USER);
        }
        checkLoaded();
      });
      unsubscribeRefs.current.push(unsubProfile);

      // Invoices
      const unsubInvoices = subscribeToInvoices(userId, (data) => {
        setInvoices(migrateInvoices(data || []));
        checkLoaded();
      });
      unsubscribeRefs.current.push(unsubInvoices);

      // Fiscal Settings
      const unsubFiscal = subscribeToFiscalSettings(userId, (data) => {
        if (data) {
          setFiscalSettingsState({ ...DEFAULT_FISCAL_SETTINGS, ...data });
        } else {
          setFiscalSettingsState(DEFAULT_FISCAL_SETTINGS);
        }
        checkLoaded();
      });
      unsubscribeRefs.current.push(unsubFiscal);

      // Pension Plan
      const unsubPension = subscribeToPensionPlan(userId, (data) => {
        if (data) {
          setPensionPlanState({ ...DEFAULT_PENSION_PLAN, ...data });
        } else {
          setPensionPlanState(DEFAULT_PENSION_PLAN);
        }
        checkLoaded();
      });
      unsubscribeRefs.current.push(unsubPension);

      // Pension Contributions
      const unsubContributions = subscribeToPensionContributions(userId, (data) => {
        setPensionContributions(data || []);
        checkLoaded();
      });
      unsubscribeRefs.current.push(unsubContributions);

      // App Settings (onboarding, tour, checklist flags)
      const unsubAppSettings = subscribeToAppSettings(userId, (data) => {
        setAppSettings(data || {});
        checkLoaded();
      });
      unsubscribeRefs.current.push(unsubAppSettings);

      // Clients
      const unsubClients = subscribeToClients(userId, (data) => {
        setClients(data || []);
        checkLoaded();
      });
      unsubscribeRefs.current.push(unsubClients);

      // Timesheets
      const unsubTimesheets = subscribeToTimesheets(userId, (data) => {
        setTimesheets(data || []);
        checkLoaded();
      });
      unsubscribeRefs.current.push(unsubTimesheets);

      // F24
      const unsubF24 = subscribeToF24(userId, (data) => {
        setF24List(migrateF24List(data || []));
        checkLoaded();
      });
      unsubscribeRefs.current.push(unsubF24);

      // Fiscal By Year (dati fiscali storicizzati)
      const unsubFiscalByYear = subscribeToFiscalByYear(userId, (data) => {
        setFiscalByYearState(data || {});
        checkLoaded();
      });
      unsubscribeRefs.current.push(unsubFiscalByYear);
    } catch (err) {
      console.error("Firestore subscription error:", err);
      setSyncError("Errore di sincronizzazione. I dati potrebbero non essere aggiornati.");
      setIsLoading(false);
    }

    return () => {
      unsubscribeRefs.current.forEach((unsub) => unsub());
      unsubscribeRefs.current = [];
    };
  }, [userId, isAuthenticated]);

  // ========== PERSIST TO LOCALSTORAGE (quando non autenticato) ==========
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (!isAuthenticated) {
      const payload = {
        user,
        darkMode,
        selectedYear,
        invoices,
        fiscalSettings,
        fiscalByYear,
        pensionPlan: {
          ...pensionPlan,
          contributions: pensionContributions,
        },
        f24: f24List,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    }
  }, [user, darkMode, selectedYear, invoices, fiscalSettings, fiscalByYear, pensionPlan, pensionContributions, f24List, isAuthenticated]);

  // ========== SETTERS CON FIRESTORE ==========

  const setUser = useCallback(async (newUser) => {
    const updated = typeof newUser === 'function' ? newUser(user) : newUser;
    setUserState(updated);
    if (isAuthenticated) {
      try {
        await saveUserProfile(userId, updated);
      } catch (err) {
        console.error("Error saving user profile:", err);
        setSyncError("Errore nel salvare il profilo.");
      }
    }
  }, [isAuthenticated, userId, user]);

  const setFiscalSettings = useCallback(async (newSettings) => {
    const updated = typeof newSettings === 'function' ? newSettings(fiscalSettings) : newSettings;
    setFiscalSettingsState(updated);
    if (isAuthenticated) {
      try {
        await saveFiscalSettings(userId, updated);
      } catch (err) {
        console.error("Error saving fiscal settings:", err);
        setSyncError("Errore nel salvare le impostazioni fiscali.");
      }
    }
  }, [isAuthenticated, userId, fiscalSettings]);

  // ===== Dati fiscali storicizzati per anno (saldo/acconti) =====
  // Restituisce sempre la shape completa, con merge profondo dei default.
  const getFiscalDataForYear = useCallback((year) => {
    const stored = fiscalByYear[year] || {};
    return {
      ...DEFAULT_FISCAL_YEAR,
      ...stored,
      accontiVersati: { ...DEFAULT_FISCAL_YEAR.accontiVersati, ...(stored.accontiVersati || {}) },
      inpsAccontiVersati: { ...DEFAULT_FISCAL_YEAR.inpsAccontiVersati, ...(stored.inpsAccontiVersati || {}) },
      saldoVersato: { ...DEFAULT_FISCAL_YEAR.saldoVersato, ...(stored.saldoVersato || {}) },
    };
  }, [fiscalByYear]);

  const setFiscalDataForYear = useCallback(async (year, data) => {
    const updatedYear = { ...getFiscalDataForYear(year), ...data };
    const merged = { ...fiscalByYear, [year]: updatedYear };
    setFiscalByYearState(merged);
    if (isAuthenticated) {
      try {
        await saveFiscalByYear(userId, merged);
      } catch (err) {
        console.error("Error saving fiscal by year:", err);
        setSyncError("Errore nel salvare i dati fiscali annuali.");
      }
    }
  }, [fiscalByYear, getFiscalDataForYear, isAuthenticated, userId]);

  // Migrazione one-time: fiscalSettings.contributiInpsPrecedenti
  // → fiscalByYear[anno precedente].contributiInpsVersati. Idempotente via flag persistito.
  const fiscalMigrationDone = useRef(false);
  useEffect(() => {
    if (isLoading || fiscalMigrationDone.current) return;
    fiscalMigrationDone.current = true; // valuta una sola volta per sessione
    if (fiscalByYear._migratedContributiInpsPrec) return; // già migrato in passato
    const prevYear = selectedYear - 1;
    const legacy = Number(fiscalSettings.contributiInpsPrecedenti) || 0;
    const target = Number(fiscalByYear[prevYear]?.contributiInpsVersati) || 0;
    const prevData = (legacy > 0 && target === 0)
      ? { ...getFiscalDataForYear(prevYear), contributiInpsVersati: legacy }
      : getFiscalDataForYear(prevYear);
    const merged = { ...fiscalByYear, [prevYear]: prevData, _migratedContributiInpsPrec: true };
    setFiscalByYearState(merged);
    if (isAuthenticated) {
      saveFiscalByYear(userId, merged).catch((e) => console.error("Migrazione fiscalByYear:", e));
    }
  }, [isLoading, fiscalByYear, fiscalSettings.contributiInpsPrecedenti, selectedYear, getFiscalDataForYear, isAuthenticated, userId]);

  // Ref a fiscalByYear per confronto senza creare dipendenze cicliche nell'effetto.
  const fiscalByYearRef = useRef(fiscalByYear);
  useEffect(() => { fiscalByYearRef.current = fiscalByYear; }, [fiscalByYear]);

  // Sincronizza fiscalByYear con gli F24 PAGATI (derivazione idempotente).
  // Scrive solo gli anni che hanno F24; gli anni pregressi (solo onboarding) restano intatti.
  useEffect(() => {
    if (isLoading) return;
    const agg = aggregateF24PaidByYear(f24List);
    const base = fiscalByYearRef.current || {};
    let next = base;
    let changed = false;
    for (const [year, derived] of Object.entries(agg)) {
      const cur = base[year] || {};
      const same =
        JSON.stringify(cur.accontiVersati || null) === JSON.stringify(derived.accontiVersati) &&
        JSON.stringify(cur.inpsAccontiVersati || null) === JSON.stringify(derived.inpsAccontiVersati) &&
        JSON.stringify(cur.saldoVersato || null) === JSON.stringify(derived.saldoVersato);
      if (!same) {
        next = { ...next, [year]: { ...DEFAULT_FISCAL_YEAR, ...cur, ...derived } };
        changed = true;
      }
    }
    if (changed) {
      setFiscalByYearState(next);
      if (isAuthenticated) {
        saveFiscalByYear(userId, next).catch((e) => console.error("Sync fiscalByYear da F24:", e));
      }
    }
  }, [f24List, isLoading, isAuthenticated, userId]);

  const updateAppSettings = useCallback(async (updates) => {
    const merged = { ...appSettings, ...updates };
    setAppSettings(merged);
    if (isAuthenticated) {
      try {
        await saveAppSettings(userId, merged);
      } catch (err) {
        console.error("Error saving app settings:", err);
      }
    }
  }, [isAuthenticated, userId, appSettings]);

  // ========== COMPUTED VALUES ==========

  const invoicesForYear = useMemo(() => {
    return invoices.filter((inv) => {
      if (inv.paid) {
        const dateToUse = inv.paymentDate || inv.issueDate;
        if (!dateToUse) return false;
        return new Date(dateToUse).getFullYear() === selectedYear;
      }
      if (!inv.issueDate) return false;
      return new Date(inv.issueDate).getFullYear() === selectedYear;
    });
  }, [invoices, selectedYear]);

  const clientCatalog = useMemo(() => {
    const set = new Set();
    // Nomi da anagrafica clienti
    for (const c of clients) {
      const name = (c.name || "").trim();
      if (name) set.add(name.replace(/\s+/g, " "));
    }
    // Nomi da fatture
    for (const inv of invoices) {
      const name = (inv.clientName || "").trim();
      if (!name) continue;
      set.add(name.replace(/\s+/g, " "));
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, "it"));
  }, [invoices, clients]);

  // Per il numero fattura, filtra SEMPRE per issueDate (non paymentDate)
  const invoicesIssuedInYear = useMemo(() => {
    return invoices.filter((inv) => {
      if (!inv.issueDate) return false;
      return new Date(inv.issueDate).getFullYear() === selectedYear;
    });
  }, [invoices, selectedYear]);

  const nextSeq = useMemo(() => {
    let max = 0;
    for (const inv of invoicesIssuedInYear) {
      // Usa seq solo se è un valore ragionevole (< 1000)
      // Altrimenti estrai dal codice fattura
      let s = Number(inv.seq);
      if (!s || s > 999) {
        s = extractSeqFromCode(inv.invoiceCode);
      }
      if (s > 0 && s < 1000 && s > max) {
        max = s;
      }
    }
    return max + 1;
  }, [invoicesIssuedInYear]);

  const nextInvoiceCode = useMemo(() => {
    return formatInvoiceCode(user.invoiceNumberTemplate, nextSeq, selectedYear);
  }, [user.invoiceNumberTemplate, nextSeq, selectedYear]);

  const getNextSeqForYear = useCallback((year) => {
    let max = 0;
    for (const inv of invoices) {
      if (!inv.issueDate) continue;
      if (new Date(inv.issueDate).getFullYear() !== year) continue;
      // Usa seq solo se è un valore ragionevole (< 1000)
      let s = Number(inv.seq);
      if (!s || s > 999) {
        s = extractSeqFromCode(inv.invoiceCode);
      }
      if (s > 0 && s < 1000 && s > max) {
        max = s;
      }
    }
    return max + 1;
  }, [invoices]);

  const getInvoiceCodeForYear = useCallback((year) => {
    const seq = getNextSeqForYear(year);
    return formatInvoiceCode(user.invoiceNumberTemplate, seq, year);
  }, [getNextSeqForYear, user.invoiceNumberTemplate]);

  // ========== INVOICE CRUD ==========

  const createInvoice = useCallback(async (draft) => {
    const now = new Date().toISOString();
    const clientName = (draft.clientName || "").trim().replace(/\s+/g, " ");
    const grossAmount = Number(draft.grossAmount) || 0;
    const seq = Number(draft.seq) || nextSeq;
    const invoiceCode = (draft.invoiceCode?.trim()) || formatInvoiceCode(user.invoiceNumberTemplate, seq, selectedYear);
    const dueDate = draft.dueDate || addDaysToDate(draft.issueDate, DEFAULT_DUE_DAYS);

    const invoice = {
      id: makeId(),
      issueDate: draft.issueDate,
      dueDate,
      invoiceCode,
      seq,
      clientName,
      grossAmount,
      paid: Boolean(draft.paid),
      paymentDate: draft.paymentDate || "",
      notes: (draft.notes || "").trim(),
      invoiceType: draft.invoiceType || "standard",
      parentInvoiceId: draft.parentInvoiceId || null,
      quoteId: draft.quoteId || null,
      percentageOfTotal: draft.percentageOfTotal != null && draft.percentageOfTotal !== ""
        ? Number(draft.percentageOfTotal)
        : null,
      createdAt: now,
      updatedAt: now,
    };
    if (draft.clientId) invoice.clientId = draft.clientId;

    if (isAuthenticated) {
      try {
        await saveInvoice(userId, invoice);
      } catch (err) {
        console.error("Error creating invoice:", err);
        setSyncError("Errore nel creare la fattura.");
      }
    } else {
      setInvoices((prev) => [invoice, ...prev]);
    }

    return invoice.id;
  }, [isAuthenticated, userId, nextSeq, user.invoiceNumberTemplate, selectedYear]);

  const updateInvoice = useCallback(async (id, patch) => {
    if (isAuthenticated) {
      try {
        await updateInvoiceFirestore(userId, id, patch);
      } catch (err) {
        console.error("Error updating invoice:", err);
        setSyncError("Errore nell'aggiornare la fattura.");
      }
    } else {
      setInvoices((prev) =>
        prev.map((inv) =>
          inv.id === id ? { ...inv, ...patch, updatedAt: new Date().toISOString() } : inv
        )
      );
    }
  }, [isAuthenticated, userId]);

  const deleteInvoice = useCallback(async (id) => {
    if (isAuthenticated) {
      try {
        await deleteInvoiceFirestore(userId, id);
      } catch (err) {
        console.error("Error deleting invoice:", err);
        setSyncError("Errore nell'eliminare la fattura.");
      }
    } else {
      setInvoices((prev) => prev.filter((inv) => inv.id !== id));
    }
  }, [isAuthenticated, userId]);

  const importInvoicesBulk = useCallback(async (drafts) => {
    const now = new Date().toISOString();

    const rows = drafts.map((d) => {
      const clientName = (d.clientName || "").trim().replace(/\s+/g, " ");
      const grossAmount = Number(d.grossAmount) || 0;
      const issueYear = d.issueDate ? new Date(d.issueDate).getFullYear() : selectedYear;
      const invoiceCode = (d.invoiceCode || "").trim();
      const seq = Number(d.seq) || extractSeqFromCode(invoiceCode) || 1;
      const dueDate = d.dueDate || addDaysToDate(d.issueDate, DEFAULT_DUE_DAYS);

      const row = {
        id: makeId(),
        issueDate: d.issueDate,
        dueDate,
        invoiceCode: invoiceCode || formatInvoiceCode(user.invoiceNumberTemplate, seq, issueYear),
        seq,
        clientName,
        grossAmount,
        paid: Boolean(d.paid),
        paymentDate: d.paymentDate || "",
        notes: (d.notes || "").trim(),
        createdAt: now,
        updatedAt: now,
      };
      if (d.clientId) row.clientId = d.clientId;
      return row;
    });

    // Filtra duplicati: skip fatture con stesso invoiceCode + clientName già esistenti
    const existingKeys = new Set(
      invoices.map((inv) => `${(inv.invoiceCode || "").toLowerCase()}|${(inv.clientName || "").toLowerCase()}`)
    );
    const newRows = rows.filter((r) => {
      const key = `${(r.invoiceCode || "").toLowerCase()}|${(r.clientName || "").toLowerCase()}`;
      return !r.invoiceCode || !existingKeys.has(key);
    });
    const skipped = rows.length - newRows.length;

    if (isAuthenticated) {
      try {
        for (const invoice of newRows) {
          await saveInvoice(userId, invoice);
        }
      } catch (err) {
        console.error("Error importing invoices:", err);
        setSyncError("Errore nell'importare le fatture.");
      }
    } else {
      setInvoices((prev) => [...newRows, ...prev]);
    }

    return { imported: newRows.length, skipped };
  }, [isAuthenticated, userId, selectedYear, user.invoiceNumberTemplate, invoices]);

  const duplicateInvoice = useCallback((draftLike) => {
    createInvoice({
      ...draftLike,
      seq: nextSeq,
    });
  }, [createInvoice, nextSeq]);

  // ========== PENSION PLAN ==========

  const updatePensionGoal = useCallback(async (monthlyGoal) => {
    const updated = { ...pensionPlan, monthlyGoal: Number(monthlyGoal) || 0 };
    setPensionPlanState(updated);
    if (isAuthenticated) {
      try {
        await savePensionPlan(userId, updated);
      } catch (err) {
        console.error("Error saving pension goal:", err);
        setSyncError("Errore nel salvare l'obiettivo pensionistico.");
      }
    }
  }, [isAuthenticated, userId, pensionPlan]);

  const addPensionContribution = useCallback(async (contribution) => {
    const newContribution = {
      id: makeId(),
      date: contribution.date,
      amount: Number(contribution.amount) || 0,
      type: contribution.type || "INPS_VOLONTARIO",
      notes: (contribution.notes || "").trim(),
    };

    if (isAuthenticated) {
      try {
        await savePensionContribution(userId, newContribution);
      } catch (err) {
        console.error("Error adding pension contribution:", err);
        setSyncError("Errore nell'aggiungere il versamento.");
      }
    } else {
      setPensionContributions((prev) => [newContribution, ...prev]);
    }
  }, [isAuthenticated, userId]);

  const updatePensionContribution = useCallback(async (id, updates) => {
    if (isAuthenticated) {
      try {
        await savePensionContribution(userId, { id, ...updates });
      } catch (err) {
        console.error("Error updating pension contribution:", err);
        setSyncError("Errore nell'aggiornare il versamento.");
      }
    } else {
      setPensionContributions((prev) =>
        prev.map((c) => (c.id === id ? { ...c, ...updates } : c))
      );
    }
  }, [isAuthenticated, userId]);

  const deletePensionContribution = useCallback(async (id) => {
    if (isAuthenticated) {
      try {
        await deletePensionContributionFirestore(userId, id);
      } catch (err) {
        console.error("Error deleting pension contribution:", err);
        setSyncError("Errore nell'eliminare il versamento.");
      }
    } else {
      setPensionContributions((prev) => prev.filter((c) => c.id !== id));
    }
  }, [isAuthenticated, userId]);

  // Combina pensionPlan con contributions per retrocompatibilità
  const pensionPlanWithContributions = useMemo(() => ({
    ...pensionPlan,
    contributions: pensionContributions,
  }), [pensionPlan, pensionContributions]);

  // ========== CLIENT CRUD ==========

  const createClient = useCallback(async (draft) => {
    const now = new Date().toISOString();
    const client = {
      id: makeId(),
      name: (draft.name || "").trim(),
      fiscalCode: (draft.fiscalCode || "").trim(),
      vatNumber: (draft.vatNumber || "").trim(),
      sdiCode: (draft.sdiCode || "").trim(),
      pecEmail: (draft.pecEmail || "").trim(),
      contactPerson: (draft.contactPerson || "").trim(),
      email: (draft.email || "").trim(),
      phone: (draft.phone || "").trim(),
      address: (draft.address || "").trim(),
      compensationType: draft.compensationType || 'daily',
      rate: Number(draft.rate) || 0,
      paymentTermsDays: Number(draft.paymentTermsDays) || 30,
      notes: (draft.notes || "").trim(),
      isActive: draft.isActive !== false,
      source: draft.source || "manual",
      createdAt: now,
      updatedAt: now,
    };

    if (isAuthenticated) {
      try {
        await saveClientFirestore(userId, client);
      } catch (err) {
        console.error("Error creating client:", err);
        setSyncError("Errore nel creare il cliente.");
      }
    } else {
      setClients((prev) => [client, ...prev]);
    }
    return client.id;
  }, [isAuthenticated, userId]);

  const updateClientFn = useCallback(async (id, patch) => {
    if (isAuthenticated) {
      try {
        await updateClientFirestore(userId, id, patch);
      } catch (err) {
        console.error("Error updating client:", err);
        setSyncError("Errore nell'aggiornare il cliente.");
      }
    } else {
      setClients((prev) =>
        prev.map((c) => c.id === id ? { ...c, ...patch, updatedAt: new Date().toISOString() } : c)
      );
    }
  }, [isAuthenticated, userId]);

  const deleteClientFn = useCallback(async (id) => {
    if (isAuthenticated) {
      try {
        await deleteClientFirestore(userId, id);
      } catch (err) {
        console.error("Error deleting client:", err);
        setSyncError("Errore nell'eliminare il cliente.");
      }
    } else {
      setClients((prev) => prev.filter((c) => c.id !== id));
    }
  }, [isAuthenticated, userId]);

  // ========== TIMESHEET CRUD ==========

  const createTimesheet = useCallback(async (draft) => {
    const now = new Date().toISOString();
    const timesheet = {
      id: makeId(),
      clientId: draft.clientId || "",
      clientName: (draft.clientName || "").trim(),
      year: draft.year,
      month: draft.month,
      status: draft.status || 'draft',
      entries: draft.entries || [],
      totalDays: draft.totalDays || 0,
      totalAmount: draft.totalAmount || 0,
      rate: draft.rate || 0,
      compensationType: draft.compensationType || 'daily',
      invoiceId: draft.invoiceId || null,
      notes: (draft.notes || "").trim(),
      createdAt: now,
      updatedAt: now,
    };

    if (isAuthenticated) {
      try {
        await saveTimesheetFirestore(userId, timesheet);
      } catch (err) {
        console.error("Error creating timesheet:", err);
        setSyncError("Errore nel creare il timesheet.");
      }
    } else {
      setTimesheets((prev) => [timesheet, ...prev]);
    }
    return timesheet.id;
  }, [isAuthenticated, userId]);

  const updateTimesheetFn = useCallback(async (id, patch) => {
    if (isAuthenticated) {
      try {
        await updateTimesheetFirestore(userId, id, patch);
      } catch (err) {
        console.error("Error updating timesheet:", err);
        setSyncError("Errore nell'aggiornare il timesheet.");
      }
    } else {
      setTimesheets((prev) =>
        prev.map((t) => t.id === id ? { ...t, ...patch, updatedAt: new Date().toISOString() } : t)
      );
    }
  }, [isAuthenticated, userId]);

  const deleteTimesheetFn = useCallback(async (id) => {
    if (isAuthenticated) {
      try {
        await deleteTimesheetFirestore(userId, id);
      } catch (err) {
        console.error("Error deleting timesheet:", err);
        setSyncError("Errore nell'eliminare il timesheet.");
      }
    } else {
      setTimesheets((prev) => prev.filter((t) => t.id !== id));
    }
  }, [isAuthenticated, userId]);

  // ========== F24 ==========

  const f24ForYear = useMemo(() => {
    return f24List.filter((f) => Number(f.anno) === selectedYear);
  }, [f24List, selectedYear]);

  const createF24 = useCallback(async (draft) => {
    const now = new Date().toISOString();
    const f24 = migrateF24Record({
      id: makeId(),
      ...draft,
      descrizione: (draft.descrizione || '').trim(),
      note: (draft.note || '').trim(),
      dataPagamento: draft.dataPagamento || '',
      anno: Number(draft.anno) || Number(draft.annoRiferimento) || selectedYear,
      annoRiferimento: Number(draft.annoRiferimento) || Number(draft.anno) || selectedYear,
      createdAt: now,
      updatedAt: now,
    });

    if (isAuthenticated) {
      try {
        await saveF24(userId, f24);
      } catch (err) {
        console.error("Error creating F24:", err);
        setSyncError("Errore nel creare l'F24.");
      }
    } else {
      setF24List((prev) => [f24, ...prev]);
    }
    return f24.id;
  }, [isAuthenticated, userId, selectedYear]);

  const updateF24Fn = useCallback(async (id, patch) => {
    const existing = f24List.find((f) => f.id === id);
    const merged = migrateF24Record({
      ...(existing || {}),
      ...patch,
      id,
      updatedAt: new Date().toISOString(),
    });
    if (isAuthenticated) {
      try {
        await saveF24(userId, merged); // setDoc merge: persiste anche i campi derivati
      } catch (err) {
        console.error("Error updating F24:", err);
        setSyncError("Errore nell'aggiornare l'F24.");
      }
    } else {
      setF24List((prev) => prev.map((f) => (f.id === id ? merged : f)));
    }
  }, [isAuthenticated, userId, f24List]);

  const deleteF24Fn = useCallback(async (id) => {
    if (isAuthenticated) {
      try {
        await deleteF24Firestore(userId, id);
      } catch (err) {
        console.error("Error deleting F24:", err);
        setSyncError("Errore nell'eliminare l'F24.");
      }
    } else {
      setF24List((prev) => prev.filter((f) => f.id !== id));
    }
  }, [isAuthenticated, userId]);

  const markF24AsPaid = useCallback(async (id, dataPagamento) => {
    const payDate = dataPagamento || new Date().toISOString().split('T')[0];
    return updateF24Fn(id, { dataPagamento: payDate });
  }, [updateF24Fn]);

  // ========== MIGRAZIONE ==========

  const migrateFromLocalStorage = useCallback(async () => {
    if (!isAuthenticated) return false;
    const localData = getSavedState();
    if (!localData) return false;

    try {
      await migrateLocalDataToFirestore(userId, localData);
      clearLocalStorage();
      return true;
    } catch (err) {
      console.error("Migration error:", err);
      throw err;
    }
  }, [isAuthenticated, userId]);

  return {
    // User
    user,
    setUser,

    // UI State
    darkMode,
    toggleDarkMode,
    selectedYear,
    setSelectedYear,

    // Invoices
    invoices,
    invoicesForYear,
    clientCatalog,
    nextSeq,
    nextInvoiceCode,
    getNextSeqForYear,
    getInvoiceCodeForYear,
    createInvoice,
    duplicateInvoice,
    updateInvoice,
    deleteInvoice,
    importInvoicesBulk,

    // Fiscal Settings
    fiscalSettings,
    setFiscalSettings,
    fiscalByYear,
    getFiscalDataForYear,
    setFiscalDataForYear,

    // Pension Plan
    pensionPlan: pensionPlanWithContributions,
    updatePensionGoal,
    addPensionContribution,
    updatePensionContribution,
    deletePensionContribution,

    // App Settings (onboarding)
    appSettings,
    updateAppSettings,

    // Clients
    clients,
    createClient,
    updateClient: updateClientFn,
    deleteClient: deleteClientFn,

    // Timesheets
    timesheets,
    createTimesheet,
    updateTimesheet: updateTimesheetFn,
    deleteTimesheet: deleteTimesheetFn,

    // F24
    f24List,
    f24ForYear,
    createF24,
    updateF24: updateF24Fn,
    deleteF24: deleteF24Fn,
    markF24AsPaid,

    // Sync State
    isLoading,
    isSynced,
    syncError,
    clearSyncError: () => setSyncError(null),

    // Migration
    migrateFromLocalStorage,

    // Auth state
    isAuthenticated,
  };
}

// Provider che wrappa l'app
export function AppStateProvider({ children, userId }) {
  const state = useAppStateInternal(userId);
  return (
    <AppStateContext.Provider value={state}>
      {children}
    </AppStateContext.Provider>
  );
}

// Hook pubblico
export function useAppState() {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error("useAppState must be used within an AppStateProvider");
  }
  return context;
}

// Export helpers
export { addDaysToDate, DEFAULT_DUE_DAYS, DEDUCTIBILITY_LIMIT };
